/**
 * Branch Management - View and manage branches for a club
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Building2, MapPin } from 'lucide-react';
import apiService from '../../services/api';
import { Section, Card, CardContent, Badge, Button, AdminPageLayout } from '../../components';
import {
  AttributeStack,
  AttributeGrid
} from '../../components/admin/patterns';
import {
  Listing,
  ListingActionBar,
  ListingFilter,
  ListingItem
} from '../../components/listing/v2';
import {
  EditAction,
  DeleteAction,
  ViewAction,
  CreateAction
} from '../../components/listing/v2/ListingAction';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';

const BranchManagement = () => {
  const navigate = useNavigate();
  const { club_id } = useParams();
  const { showSuccess, showError } = useToast();

  const [branches, setBranches] = useState([]);
  const [clubInfo, setClubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    type: 'all'
  });
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const LOCATION_TYPES = [
    { value: 'main_facility', label: 'Main Facility' },
    { value: 'branch_facility', label: 'Branch Facility' },
    { value: 'training_ground', label: 'Training Ground' },
    { value: 'academy', label: 'Academy' },
    { value: 'community_center', label: 'Community Center' }
  ];

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // Try to load club info only if we have a specific club_id (platform admin viewing specific club)
      if (club_id) {
        try {
          const club = await apiService.getClub(club_id);
          setClubInfo(club);
        } catch (clubErr) {
          // Silently fail - club info is optional for display purposes
          console.log('Could not load club info (may not have platform admin permissions)');
        }
      }

      // Load branches for current user's club (from JWT token)
      const branchesList = await apiService.getClubBranches(club_id);
      setBranches(branchesList || []);

    } catch (err) {
      console.error('Error loading data:', err);
      showError('Failed to load branches');
    } finally {
      setLoading(false);
    }
  }, [club_id, showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const filteredBranches = branches.filter(branch => {
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      if (!branch.name?.toLowerCase().includes(searchTerm) &&
        !branch.city?.toLowerCase().includes(searchTerm) &&
        !branch.state?.toLowerCase().includes(searchTerm) &&
        !branch.display_name?.toLowerCase().includes(searchTerm)) {
        return false;
      }
    }
    if (filters.status !== 'all' && branch.status !== filters.status) {
      return false;
    }
    if (filters.type !== 'all' && branch.location_type !== filters.type) {
      return false;
    }
    return true;
  });

  const getTypeLabel = (locationType) => {
    const type = LOCATION_TYPES.find(t => t.value === locationType);
    return type ? type.label : locationType || 'Unknown';
  };

  const handleCreateBranch = () => {
    if (club_id) {
      navigate(`/admin/clubs/${club_id}/branches/create`);
    } else {
      navigate('/admin/branches/create');
    }
  };

  const handleViewBranch = (branch) => {
    navigate(`/admin/branches/${branch.id}`);
  };

  const handleEditBranch = (branch) => {
    navigate(`/admin/branches/${branch.id}/edit`);
  };

  const handleDeactivateBranch = (branch) => {
    setShowDeactivateModal(branch);
  };

  const confirmDeactivateBranch = async () => {
    if (!showDeactivateModal) return;

    try {
      await apiService.deactivateBranch(showDeactivateModal.id);
      showSuccess(`Branch "${showDeactivateModal.name}" deactivated successfully`);
      setShowDeactivateModal(null);
      await loadData();
    } catch (err) {
      console.error('Error deactivating branch:', err);
      showError('Failed to deactivate branch');
    }
  };

  const handleActivateBranch = (branch) => {
    setShowActivateModal(branch);
  };

  const confirmActivateBranch = async () => {
    if (!showActivateModal) return;

    try {
      await apiService.activateBranch(showActivateModal.id);
      showSuccess(`Branch "${showActivateModal.name}" activated successfully`);
      setShowActivateModal(null);
      await loadData();
    } catch (err) {
      console.error('Error activating branch:', err);
      showError('Failed to activate branch');
    }
  };

  const handleDeleteBranch = (branch) => {
    setShowDeleteModal(branch);
  };

  const confirmDeleteBranch = async () => {
    if (!showDeleteModal) return;

    setIsDeleting(true);
    try {
      await apiService.deleteBranch(showDeleteModal.id);
      showSuccess(`Branch "${showDeleteModal.name}" deleted permanently`);
      setShowDeleteModal(null);
      await loadData();
    } catch (err) {
      console.error('Error deleting branch:', err);
      showError('Failed to delete branch');
    } finally {
      setIsDeleting(false);
    }
  };

  const filterBar = (
    <ListingFilter
      searchValue={filters.search}
      onSearchChange={(val) => handleFilterChange('search', val)}
      searchPlaceholder="Search branches by name, city, or state..."
      filters={[
        {
          name: 'status',
          label: 'Status',
          options: [
            { value: 'all', label: 'All Statuses' },
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' }
          ]
        },
        {
          name: 'type',
          label: 'Type',
          options: [
            { value: 'all', label: 'All Types' },
            ...LOCATION_TYPES
          ]
        }
      ]}
      activeFilters={{ status: filters.status, type: filters.type }}
      onFilterChange={(name, val) => handleFilterChange(name, val)}
      onClearFilters={() => setFilters({ search: '', status: 'all', type: 'all' })}
    />
  );

  const headerActions = (
    <ListingActionBar
      rightSection={
        <CreateAction onClick={handleCreateBranch} label="INITIALIZE NEW BRANCH" />
      }
    />
  );

  const renderBranchActions = (branch) => {
    const actions = [
      <ViewAction size="xs" key="view" onClick={() => handleViewBranch(branch)} />,
      <EditAction size="xs" key="edit" onClick={() => handleEditBranch(branch)} />
    ];

    if (branch.status === 'active') {
      actions.push(
        <Button
          key="deactivate"
          variant="outline"
          size="xs"
          onClick={() => handleDeactivateBranch(branch)}
          style={{ color: 'var(--color-warning)', borderColor: 'var(--color-warning)' }}
        >
          Deactivate
        </Button>
      );
    } else {
      actions.push(
        <Button
          key="activate"
          variant="primary"
          size="xs"
          onClick={() => handleActivateBranch(branch)}
        >
          Activate
        </Button>
      );
    }

    actions.push(
      <DeleteAction size="xs" key="delete" onClick={() => handleDeleteBranch(branch)} />
    );

    return <>{actions}</>;
  };

  return (
    <AdminPageLayout
      title="BRANCH GOVERNANCE"
      subtitle={clubInfo ? `Operational nodes for ${clubInfo.name}` : 'Regional Infrastructure Management'}
      maxWidth="1400px"
    >
      {clubInfo && (
        <Section title="PARENT ORGANIZATION">
          <Card level={1} variant="feature">
            <CardContent>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: 'var(--surface-lvl3)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-primary)'
                  }}>
                    <Building2 size={32} />
                  </div>
                  <div>
                    <h3 style={{ margin: '0 0 0.25rem 0', fontWeight: '700', fontSize: '1.25rem' }}>{clubInfo.name}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '0.9rem', color: 'var(--color-text-tertiary)' }}>
                        Tenant Identifier: <code style={{ backgroundColor: 'var(--surface-lvl3)', padding: '2px 6px', borderRadius: '4px', fontSize: '0.8rem' }}>{clubInfo.slug}</code>
                      </span>
                      <Badge variant="outline">MAIN BRANCH</Badge>
                    </div>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '2rem', fontWeight: '800', lineHeight: 1 }}>{branches.length}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: '0.5rem' }}>Active Branches</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </Section>
      )}

      <Section title="REGIONAL INFRASTRUCTURE">
        <Listing
          loading={loading}
          items={filteredBranches}
          actions={headerActions}
          filters={filterBar}
          variant="list"
          renderItem={(branch) => (
            <ListingItem
              key={branch.id}
              title={branch.name}
              subtitle={branch.display_name || `${branch.city}, ${branch.state}`}
              icon={<Building2 size={32} />}
              iconVariant={branch.status === 'active' ? 'primary' : 'secondary'}
              actions={renderBranchActions(branch)}
            >
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--color-border-light)' }}>
                <AttributeGrid columns={3} gap="2rem">
                  <AttributeStack
                    label="Location"
                    value={
                      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MapPin size={14} /> {branch.city}, {branch.state}
                      </span>
                    }
                  />
                  <AttributeStack
                    label="Type"
                    value={<Badge size="sm" variant="outline">{getTypeLabel(branch.location_type)?.toUpperCase()}</Badge>}
                  />
                  <AttributeStack
                    label="Status"
                    value={
                      branch.status === 'active'
                        ? <Badge variant="success" size="sm">ACTIVE NODE</Badge>
                        : <Badge variant="warning" size="sm">INACTIVE</Badge>
                    }
                  />
                </AttributeGrid>
              </div>
            </ListingItem>
          )}
          emptyMessage="No operational branches identified in this region"
        />
      </Section>

      {/* Deactivate Confirmation Dialog */}
      {showDeactivateModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeactivateModal(null)}
          onConfirm={confirmDeactivateBranch}
          title="Suspend Branch Operations"
          variant="warning"
          confirmText="Confirm Suspension"
        >
          <p>Are you sure you want to deactivate <strong>{showDeactivateModal.name}</strong>?</p>
          <p style={{ marginTop: '0.5rem', opacity: 0.7, fontSize: '0.9rem' }}>
            This will suspend all activities linked to this operational node.
          </p>
        </ConfirmDialog>
      )}

      {/* Activate Confirmation Dialog */}
      {showActivateModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowActivateModal(null)}
          onConfirm={confirmActivateBranch}
          title="Restore Branch Operations"
          variant="info"
          confirmText="Initialize Restore"
        >
          <p>Are you sure you want to reactivate <strong>{showActivateModal.name}</strong>?</p>
          <p style={{ marginTop: '0.5rem', opacity: 0.7, fontSize: '0.9rem' }}>
            This will restore full operational status to this regional node.
          </p>
        </ConfirmDialog>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteModal && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setShowDeleteModal(null)}
          onConfirm={confirmDeleteBranch}
          title="Permanent Infrastructure Removal"
          variant="danger"
          confirmText="Finalize Deletion"
          loading={isDeleting}
        >
          <p>Are you sure you want to permanently decommission <strong>{showDeleteModal.name}</strong>?</p>
          <p style={{ marginTop: '0.5rem', color: 'var(--color-error)', fontWeight: '600', fontSize: '0.9rem' }}>
            Warning: This action is irreversible and will purge all data associated with this branch.
          </p>
        </ConfirmDialog>
      )}
    </AdminPageLayout>
  );
};

export default BranchManagement;
